import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import {
  CalendarDays,
  Type,
  BookOpenText,
  Image as ImageIcon,
  Images,
  Clock3,
  FileText,
  Heart,
  Upload,
  Plus,
  ChevronUp,
  ChevronDown,
  Trash2,
  LayoutPanelTop,
  Columns2,
  Settings2,
  Crop,
  Palette,
  PanelBottomOpen,
  X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE = {
  names: 'Sofia & Daniel',
  dateISO: '2026-10-14',
  location: 'The Rock Hotel, Gibraltar',
  story:
    'We met by the sea and found home in each other. Gibraltar became our favorite place to dream, and now we are so excited to celebrate this next chapter with everyone we love.',
  travel:
    'Fly into Gibraltar Airport or Malaga. Recommended hotels are near Main Street and Ocean Village. Transfers and local tips will be shared after RSVP.',
  details: {
    ceremonyTime: '15:00',
    ceremonyPlace: 'St. Mary the Crowned, Gibraltar',
    receptionTime: '18:30',
    receptionPlace: 'The Rock Hotel Ballroom',
  },
  timeline: [
    { time: '14:00', title: 'Guest Arrival', note: 'Welcome drinks on arrival' },
    { time: '15:00', title: 'Ceremony', note: 'Main ceremony hall' },
    { time: '16:15', title: 'Photos & Canapés', note: 'Terrace' },
    { time: '18:30', title: 'Reception Dinner', note: 'Ballroom' },
  ],
  heroImage: 'https://images.pexels.com/photos/11749510/pexels-photo-11749510.jpeg',
  gallery: [
    'https://images.unsplash.com/photo-1707194226105-c3bd1b3c9b68',
    'https://images.unsplash.com/photo-1707194227157-638538836b10',
    'https://images.pexels.com/photos/34681510/pexels-photo-34681510.jpeg',
  ],
};

const SECTION_OPTIONS = [
  { value: 'title', label: 'Home' },
  { value: 'story', label: 'Our Story' },
  { value: 'photo', label: 'Main photo' },
  { value: 'photo_gallery', label: 'Your Photos' },
  { value: 'timeline', label: 'Schedule' },
  { value: 'wedding_details', label: 'Wedding Details' },
  { value: 'text_block', label: 'Travel Info' },
  { value: 'rsvp', label: 'RSVP' },
  { value: 'photo_upload', label: 'Photo sharing message' },
];

const SECTION_TILE_META = {
  title: { label: 'Home', icon: Type, mini: 'Aa' },
  story: { label: 'Our Story', icon: BookOpenText, mini: 'Text' },
  photo: { label: 'Main photo', icon: ImageIcon, mini: 'Image' },
  photo_gallery: { label: 'Gallery', icon: Images, mini: 'Grid' },
  timeline: { label: 'Schedule', icon: Clock3, mini: 'Time' },
  wedding_details: { label: 'Details', icon: CalendarDays, mini: 'Info' },
  text_block: { label: 'Travel', icon: FileText, mini: 'Copy' },
  rsvp: { label: 'RSVP', icon: Heart, mini: 'Form' },
  photo_upload: { label: 'Photo sharing', icon: Upload, mini: 'Msg' },
};

const LAYOUT_OPTIONS = [
  { value: 'single', label: 'Single page', helper: 'Smooth scroll website', icon: LayoutPanelTop },
  { value: 'multi', label: 'Multi-page', helper: 'Home / Story / Details / RSVP', icon: Columns2 },
];

const DATE_OPTIONS = [
  { value: 'uk-long', preview: '14 October 2026', label: 'UK Long' },
  { value: 'us-long', preview: 'October 14, 2026', label: 'US Long' },
  { value: 'numeric', preview: '14/10/2026', label: 'Numeric' },
];

const TYPOGRAPHY_OPTIONS = [
  {
    value: 'timeless-seriffed',
    label: 'Timeless Serif',
    helper: 'Cormorant headings + Manrope body',
    style: { fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem' },
  },
  {
    value: 'editorial-grand',
    label: 'Editorial Grand',
    helper: 'Playfair headings + clean sans body',
    style: { fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', letterSpacing: '0.02em' },
  },
  {
    value: 'refined-contemporary',
    label: 'Refined Contemporary',
    helper: 'Elegant serif with slightly tighter rhythm',
    style: { fontFamily: "'Playfair Display', serif", fontSize: '1.15rem', letterSpacing: '0.05em' },
  },
  {
    value: 'modern-editorial',
    label: 'Modern Editorial',
    helper: 'Balanced modern sans with elegant display moments',
    style: { fontFamily: "'Manrope', sans-serif", fontSize: '1.02rem', letterSpacing: '0.02em' },
  },
];

const COLOR_THEME_OPTIONS = [
  {
    value: 'stone-ivory',
    label: 'Stone Ivory',
    pageBg: '#f8f6f2',
    pageText: '#1f1b16',
    surface: '#ffffff',
    border: '#ddd5c9',
    accent: '#5c0120',
    accentText: '#ffffff',
    preview: ['#f8f6f2', '#ffffff', '#5c0120'],
  },
  {
    value: 'blush-champagne',
    label: 'Blush Champagne',
    pageBg: '#fcf5f6',
    pageText: '#3c2430',
    surface: '#fffdf9',
    border: '#e8d4da',
    accent: '#8f2f56',
    accentText: '#ffffff',
    preview: ['#fcf5f6', '#fffdf9', '#8f2f56'],
  },
  {
    value: 'coastal-pearl',
    label: 'Coastal Pearl',
    pageBg: '#f2f7fb',
    pageText: '#1c3347',
    surface: '#ffffff',
    border: '#c7d9e6',
    accent: '#2d5f86',
    accentText: '#ffffff',
    preview: ['#f2f7fb', '#ffffff', '#2d5f86'],
  },
  {
    value: 'sage-linen',
    label: 'Sage Linen',
    pageBg: '#f1f6f1',
    pageText: '#27372a',
    surface: '#ffffff',
    border: '#cedacb',
    accent: '#3f6a4b',
    accentText: '#ffffff',
    preview: ['#f1f6f1', '#ffffff', '#3f6a4b'],
  },
];

const SPACING_STYLE_OPTIONS = [
  { value: 'compact', label: 'Compact', helper: 'Tighter vertical rhythm for dense layouts' },
  { value: 'balanced', label: 'Balanced', helper: 'Moderate spacing for general readability' },
  { value: 'airy', label: 'Airy', helper: 'Breathing room between sections' },
  { value: 'airy-plus', label: 'Airy Plus', helper: 'Luxury-level generous spacing' },
];

const STYLE_PACK_OPTIONS = [
  {
    value: 'editorial',
    label: 'Editorial',
    helper: 'Elegant magazine-like rhythm',
    colorTheme: 'stone-ivory',
    typographyStyle: 'editorial-grand',
    spacingStyle: 'airy',
  },
  {
    value: 'coastal',
    label: 'Coastal',
    helper: 'Fresh and light with calm blue tones',
    colorTheme: 'coastal-pearl',
    typographyStyle: 'modern-editorial',
    spacingStyle: 'balanced',
  },
  {
    value: 'classic',
    label: 'Classic',
    helper: 'Traditional formal invitation feel',
    colorTheme: 'sage-linen',
    typographyStyle: 'timeless-seriffed',
    spacingStyle: 'compact',
  },
  {
    value: 'luxury',
    label: 'Luxury',
    helper: 'High-end statement composition',
    colorTheme: 'blush-champagne',
    typographyStyle: 'refined-contemporary',
    spacingStyle: 'airy-plus',
  },
];

const buildStyleSignature = ({ stylePack, color, typography, spacing }) => [
  stylePack || 'custom',
  color,
  typography,
  spacing,
].join('|');

const hexToRgb = (hexValue) => {
  if (!hexValue || typeof hexValue !== 'string' || !hexValue.startsWith('#')) return null;
  const hex = hexValue.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map((char) => `${char}${char}`).join('')
    : hex;
  if (normalized.length !== 6) return null;
  const int = parseInt(normalized, 16);
  if (Number.isNaN(int)) return null;
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};

const getRelativeLuminance = (hexValue) => {
  const rgb = hexToRgb(hexValue);
  if (!rgb) return 1;
  const convert = (channel) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  const r = convert(rgb.r);
  const g = convert(rgb.g);
  const b = convert(rgb.b);
  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
};

const blendHex = (hexA, hexB, ratio = 0.5) => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return hexA || '#5C0120';
  const blend = (x, y) => Math.round((x * (1 - ratio)) + (y * ratio));
  const toHex = (value) => value.toString(16).padStart(2, '0');
  const r = blend(a.r, b.r);
  const g = blend(a.g, b.g);
  const bChannel = blend(a.b, b.b);
  return `#${toHex(r)}${toHex(g)}${toHex(bChannel)}`;
};

const MICROCOPY_AUDIT_SELECTOR = [
  '[data-testid="v2-builder-controls-loading-note"]',
  '[data-testid="v2-mood-preview-helper-text"]',
  '[data-testid^="v2-style-pack-helper-"]',
  '[data-testid^="v2-layout-helper-"]',
  '[data-testid^="v2-typography-helper-"]',
  '[data-testid^="v2-spacing-helper-"]',
  '[data-testid^="v2-section-mini-preview-"]',
  '[data-testid="v2-builder-empty-sections-hint"]',
].join(', ');

const TEMPLATE_META = {
  'editorial-elegant': {
    name: 'Editorial Elegant',
    description: 'Typography-led and minimal with left editorial rhythm.',
    hero: 'text',
    nav: 'top-underlined',
    fontHeading: "'Cormorant Garamond', serif",
    fontBody: "'Inter', sans-serif",
    pageClass: 'bg-[#f9f7f2] text-[#171717]',
  },
  'photo-story': {
    name: 'Photo Story',
    description: 'Full-image emotional hero and cinematic storytelling sections.',
    hero: 'full-image',
    nav: 'minimal-scroll',
    fontHeading: "'Playfair Display', serif",
    fontBody: "'Lato', sans-serif",
    pageClass: 'bg-white text-[#111827]',
  },
  'classic-invitation': {
    name: 'Classic Invitation',
    description: 'Formal centered invitation with decorative framing.',
    hero: 'decorative',
    nav: 'center-formal',
    fontHeading: "'Cormorant Garamond', serif",
    fontBody: "'Libre Baskerville', serif",
    pageClass: 'bg-[#fff8ef] text-[#3a2a1e]',
  },
  'modern-clean': {
    name: 'Modern Clean',
    description: 'Structured split layout with clear contemporary hierarchy.',
    hero: 'split',
    nav: 'flat-bar',
    fontHeading: "'Poppins', sans-serif",
    fontBody: "'Inter', sans-serif",
    pageClass: 'bg-[#f5f8fc] text-[#0f172a]',
  },
  'modern-luxury': {
    name: 'Modern Luxury',
    description: 'High-contrast luxury composition with editorial framing and premium spacing.',
    hero: 'luxury-frame',
    nav: 'luxury-line',
    fontHeading: "'Playfair Display', serif",
    fontBody: "'Manrope', sans-serif",
    pageClass: 'bg-[#fbf7f2] text-[#1f1b17]',
  },
};

const DEFAULT_SECTIONS = [
  { id: 's-title', type: 'title' },
  { id: 's-story', type: 'story' },
  { id: 's-wedding-details', type: 'wedding_details' },
  { id: 's-timeline', type: 'timeline' },
  { id: 's-travel', type: 'text_block' },
];

const DEFAULT_SECTION_IMAGES = {
  story: { url: '', orientation: 'landscape' },
  wedding_details: { url: '', orientation: 'landscape' },
  timeline: { url: '', orientation: 'landscape' },
  text_block: { url: '', orientation: 'landscape' },
};

const pageForSectionType = (type) => {
  if (['title', 'photo', 'photo_upload'].includes(type)) return 'home';
  if (['story', 'photo_gallery', 'text_block'].includes(type)) return 'story';
  if (['wedding_details', 'timeline'].includes(type)) return 'details';
  if (['rsvp'].includes(type)) return 'rsvp';
  return 'home';
};

const formatWeddingDate = (isoDate, format) => {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  if (format === 'uk-long') return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  if (format === 'us-long') return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return date.toLocaleDateString('en-GB');
};

const detectImageOrientation = (file) => new Promise((resolve) => {
  if (!file || typeof window === 'undefined') {
    resolve('landscape');
    return;
  }

  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const nextOrientation = image.height > image.width ? 'portrait' : 'landscape';
    URL.revokeObjectURL(objectUrl);
    resolve(nextOrientation);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    resolve('landscape');
  };
  image.src = objectUrl;
});

const renderSectionTitle = (text, uppercase) => (
  <h3 className={`text-2xl font-semibold mb-3 ${uppercase ? 'uppercase tracking-[0.14em] text-base' : ''}`}>{text}</h3>
);

const PreviewRsvp = ({ theme, uppercaseHeadings }) => {
  const [count, setCount] = useState(1);
  const cards = Array.from({ length: count }, (_, i) => i);

  return (
    <section
      id="rsvp"
      className="border p-5"
      style={{ backgroundColor: theme.surface, borderColor: theme.border }}
      data-testid="v2-builder-rsvp-section"
    >
      {renderSectionTitle('RSVP', uppercaseHeadings)}
      <p className="text-sm mb-4" style={{ color: theme.pageText, opacity: 0.75 }}>You can RSVP for multiple guests.</p>
      {cards.map((index) => (
        <div
          key={`guest-${index}`}
          className="border p-3 mb-3"
          style={{ borderColor: theme.border, backgroundColor: '#ffffffcc' }}
          data-testid={`v2-builder-rsvp-guest-${index}`}
        >
          <p className="font-medium mb-2">Guest {index + 1}</p>
          <div className="grid grid-cols-1 gap-2">
            <Input placeholder="Name" data-testid={`v2-builder-rsvp-name-${index}`} />
            <Input placeholder="Email" data-testid={`v2-builder-rsvp-email-${index}`} />
            <select className="w-full rounded-md border border-stone-300 p-2 text-sm" data-testid={`v2-builder-rsvp-attendance-${index}`}>
              <option>Attendance: Yes</option>
              <option>Attendance: No</option>
            </select>
            <select className="w-full rounded-md border border-stone-300 p-2 text-sm" data-testid={`v2-builder-rsvp-dietary-${index}`}>
              <option>None</option>
              <option>Vegetarian</option>
              <option>Vegan</option>
              <option>Gluten-free</option>
              <option>Halal</option>
              <option>Kosher</option>
              <option>Other</option>
            </select>
            <Input placeholder="If Other, please specify (optional)" data-testid={`v2-builder-rsvp-dietary-other-${index}`} />
          </div>
        </div>
      ))}
      <button type="button" className="text-sm font-medium underline" onClick={() => setCount((v) => v + 1)} data-testid="v2-builder-add-guest-btn">
        + Add guest
      </button>
      <Button className="w-full mt-4" style={{ backgroundColor: theme.accent, color: theme.accentText }} data-testid="v2-builder-rsvp-submit-btn">Submit RSVP</Button>
    </section>
  );
};

const renderSection = ({ section, sectionIndex, controls, templateMeta, theme, sample }) => {
  const titleUpper = controls.uppercaseHeadings;
  const headingFontMap = {
    'timeless-seriffed': "'Cormorant Garamond', serif",
    'editorial-grand': "'Playfair Display', serif",
    'refined-contemporary': "'Playfair Display', serif",
    'modern-editorial': "'Manrope', sans-serif",
  };
  const bodyFontMap = {
    'timeless-seriffed': "'Manrope', sans-serif",
    'editorial-grand': "'Manrope', sans-serif",
    'refined-contemporary': "'Inter', sans-serif",
    'modern-editorial': "'Manrope', sans-serif",
  };
  const headingStyle = {
    fontFamily: headingFontMap[controls.typographyStyle] || templateMeta.fontHeading,
  };
  const bodyStyle = { fontFamily: bodyFontMap[controls.typographyStyle] || templateMeta.fontBody };
  const sectionPaddingMap = {
    compact: 'p-5',
    balanced: 'p-6',
    airy: 'p-7',
    'airy-plus': 'p-8',
  };
  const sectionPaddingClass = sectionPaddingMap[controls.spacingStyle] || 'p-6';
  const imageAspectClass = 'aspect-[16/9]';
  const imageFitClass = 'object-cover object-center';
  const sectionImage = sample.sectionImages?.[section.type];
  const isPortraitSectionImage = sectionImage?.orientation === 'portrait';
  const portraitImageLeft = sectionIndex % 2 === 0;

  if (section.type === 'title') {
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-title">
        <p className="text-xs uppercase tracking-[0.2em] mb-2">{templateMeta.name}</p>
        <h2 className={`${titleUpper ? 'uppercase tracking-[0.12em] text-3xl' : 'text-4xl'} font-semibold`} style={headingStyle}>{sample.names}</h2>
        {sample.location && <p className="mt-2 text-stone-600" style={bodyStyle}>{controls.formattedDate} · {sample.location}</p>}
        {controls.showCountdown && <p className="mt-3 text-sm">Countdown: <strong>{controls.countdownDays} days to go</strong></p>}
      </section>
    );
  }

  if (section.type === 'story') {
    if (!sample.story && !sectionImage?.url) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-story">
        {sectionImage?.url && isPortraitSectionImage ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {portraitImageLeft && (
              <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-story-portrait-image-left">
                <img src={sectionImage.url} alt="Story" className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
            <div>
              <h3 className={`${titleUpper ? 'uppercase tracking-[0.12em] text-base' : 'text-2xl'} font-semibold mb-3`} style={headingStyle}>Our Story</h3>
              {sample.story && <p className="leading-relaxed" style={bodyStyle}>{sample.story}</p>}
            </div>
            {!portraitImageLeft && (
              <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-story-portrait-image-right">
                <img src={sectionImage.url} alt="Story" className="w-full h-full aspect-[4/5] object-cover object-center" />
              </div>
            )}
          </div>
        ) : (
          <>
            <h3 className={`${titleUpper ? 'uppercase tracking-[0.12em] text-base' : 'text-2xl'} font-semibold mb-3`} style={headingStyle}>Our Story</h3>
            {sample.story && <p className="leading-relaxed" style={bodyStyle}>{sample.story}</p>}
            {sectionImage?.url && (
              <div className="mt-3 w-full overflow-hidden border border-stone-200" data-testid="v2-builder-story-landscape-image-wrap">
                <img src={sectionImage.url} alt="Story" className="w-full h-full aspect-[16/9] object-cover object-center" />
              </div>
            )}
          </>
        )}
      </section>
    );
  }

  if (section.type === 'photo') {
    if (!sample.heroImage) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-photo">
        {renderSectionTitle('Main Photo', titleUpper)}
        <div className={`${imageAspectClass} w-full overflow-hidden border border-stone-200`}>
          <img src={sample.heroImage} alt={sample.names} className={`w-full h-full ${imageFitClass}`} />
        </div>
      </section>
    );
  }

  if (section.type === 'photo_gallery') {
    if (!sample.gallery || sample.gallery.length === 0) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-gallery">
        {renderSectionTitle('Your Photos', titleUpper)}
        <div className="grid grid-cols-2 gap-2">
          {sample.gallery.map((src) => (
            <div key={src} className={`${imageAspectClass} overflow-hidden border border-stone-200`}>
              <img src={src} alt="Gallery" className={`w-full h-full ${imageFitClass}`} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (section.type === 'timeline') {
    if (!sample.timeline || sample.timeline.length === 0) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-timeline">
        <div className={`grid grid-cols-1 ${sectionImage?.url && isPortraitSectionImage ? 'md:grid-cols-2' : ''} gap-4 items-start`}>
          {sectionImage?.url && isPortraitSectionImage && portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-schedule-portrait-image-left">
              <img src={sectionImage.url} alt="Schedule" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
          <div>
            {renderSectionTitle('Schedule', titleUpper)}
            <div className="space-y-2">
              {sample.timeline.map((item) => (
                <div key={`${item.time}-${item.title}`} className="border-l-2 border-stone-300 pl-3 py-1">
                  <p className="font-semibold text-sm">{item.time} · {item.title}</p>
                  <p className="text-sm text-stone-600">{item.note}</p>
                </div>
              ))}
            </div>
            {sectionImage?.url && !isPortraitSectionImage && (
              <div className="mt-3 w-full overflow-hidden border border-stone-200" data-testid="v2-builder-schedule-landscape-image-wrap">
                <img src={sectionImage.url} alt="Schedule" className="w-full h-full aspect-[16/9] object-cover object-center" />
              </div>
            )}
          </div>
          {sectionImage?.url && isPortraitSectionImage && !portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-schedule-portrait-image-right">
              <img src={sectionImage.url} alt="Schedule" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
        </div>
      </section>
    );
  }

  if (section.type === 'wedding_details') {
    if (!sample.details.ceremonyPlace && !sample.details.ceremonyTime && !sample.details.receptionPlace && !sample.details.receptionTime) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-details">
        <div className={`grid grid-cols-1 ${sectionImage?.url && isPortraitSectionImage ? 'md:grid-cols-2' : ''} gap-4 items-start`}>
          {sectionImage?.url && isPortraitSectionImage && portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-details-portrait-image-left">
              <img src={sectionImage.url} alt="Wedding details" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
          <div>
            {renderSectionTitle('Wedding Details', titleUpper)}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <article className="border border-stone-200 p-3">
                <p className="text-xs uppercase tracking-[0.14em] mb-1">Ceremony</p>
                <p className="font-semibold">{sample.details.ceremonyTime}</p>
                <p className="text-sm">{sample.details.ceremonyPlace}</p>
              </article>
              <article className="border border-stone-200 p-3">
                <p className="text-xs uppercase tracking-[0.14em] mb-1">Reception</p>
                <p className="font-semibold">{sample.details.receptionTime}</p>
                <p className="text-sm">{sample.details.receptionPlace}</p>
              </article>
            </div>
            {sectionImage?.url && !isPortraitSectionImage && (
              <div className="mt-3 w-full overflow-hidden border border-stone-200" data-testid="v2-builder-details-landscape-image-wrap">
                <img src={sectionImage.url} alt="Wedding details" className="w-full h-full aspect-[16/9] object-cover object-center" />
              </div>
            )}
          </div>
          {sectionImage?.url && isPortraitSectionImage && !portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-details-portrait-image-right">
              <img src={sectionImage.url} alt="Wedding details" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
        </div>
      </section>
    );
  }

  if (section.type === 'text_block') {
    if (!sample.travel) return null;
    return (
      <section className={`border ${sectionPaddingClass}`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-textblock">
        <div className={`grid grid-cols-1 ${sectionImage?.url && isPortraitSectionImage ? 'md:grid-cols-2' : ''} gap-4 items-start`}>
          {sectionImage?.url && isPortraitSectionImage && portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-travel-portrait-image-left">
              <img src={sectionImage.url} alt="Travel" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
          <div>
            {renderSectionTitle('Travel & Info', titleUpper)}
            {sample.travel && <p>{sample.travel}</p>}
            {sectionImage?.url && !isPortraitSectionImage && (
              <div className="mt-3 w-full overflow-hidden border border-stone-200" data-testid="v2-builder-travel-landscape-image-wrap">
                <img src={sectionImage.url} alt="Travel" className="w-full h-full aspect-[16/9] object-cover object-center" />
              </div>
            )}
          </div>
          {sectionImage?.url && isPortraitSectionImage && !portraitImageLeft && (
            <div className="overflow-hidden border border-stone-200" data-testid="v2-builder-travel-portrait-image-right">
              <img src={sectionImage.url} alt="Travel" className="w-full h-full aspect-[4/5] object-cover object-center" />
            </div>
          )}
        </div>
      </section>
    );
  }

  if (section.type === 'rsvp') return <PreviewRsvp theme={theme} uppercaseHeadings={titleUpper} />;

  if (section.type === 'photo_upload') {
    if (!sample.photoCta) return null;
    return (
      <section className={`border ${sectionPaddingClass} text-center`} style={{ backgroundColor: theme.surface, borderColor: theme.border }} data-testid="v2-builder-section-photo-upload">
        {renderSectionTitle('Photo Sharing', titleUpper)}
        <p className="mb-4">{sample.photoCta}</p>
        <Button variant="outline" style={{ borderColor: theme.accent, color: theme.accent }} data-testid="v2-builder-photo-upload-btn">Upload Photos (Coming Soon)</Button>
      </section>
    );
  }

  return null;
};

const TemplateHero = ({ templateMeta, controls, theme, sample }) => {
  const heading = controls.uppercaseHeadings ? 'uppercase tracking-[0.12em]' : '';
  const headingFontMap = {
    'timeless-seriffed': "'Cormorant Garamond', serif",
    'editorial-grand': "'Playfair Display', serif",
    'refined-contemporary': "'Playfair Display', serif",
    'modern-editorial': "'Manrope', sans-serif",
  };
  const headingStyle = {
    fontFamily: headingFontMap[controls.typographyStyle] || templateMeta.fontHeading,
  };

  if (templateMeta.hero === 'full-image') {
    return (
      <section
        className="min-h-[56vh] flex items-end px-6 py-8 text-white"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0.7)), url('${sample.heroImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="v2-builder-template-hero-full-image"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.2em] mb-2">Photo Story</p>
          <h1 className={`text-4xl sm:text-5xl font-semibold ${heading}`} style={headingStyle}>{sample.names}</h1>
        </div>
      </section>
    );
  }

  if (templateMeta.hero === 'split') {
    return (
      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden" data-testid="v2-builder-template-hero-split">
        <div className="p-6 md:p-8 bg-white border-b md:border-b-0 md:border-r border-stone-200">
          <p className="text-xs uppercase tracking-[0.2em] mb-2">Modern Clean</p>
          <h1 className={`text-4xl font-semibold ${heading}`} style={headingStyle}>{sample.names}</h1>
          <p className="mt-2 text-sm text-stone-600">{controls.formattedDate} · {sample.location}</p>
        </div>
        <div className="aspect-[16/10] md:aspect-auto">
          <img src={sample.heroImage} alt={sample.names} className="w-full h-full object-cover object-[center_top]" />
        </div>
      </section>
    );
  }

  if (templateMeta.hero === 'luxury-frame') {
    return (
      <section className="px-6 py-10 border-b" style={{ backgroundColor: '#f9f3eb', borderColor: theme.border }} data-testid="v2-builder-template-hero-luxury-frame">
        <div className="border border-[#d6c5ae] bg-[#fffaf4] p-6 sm:p-8 text-center">
          <p className="text-xs uppercase tracking-[0.24em] mb-2" style={{ color: '#7f6345' }}>Modern Luxury</p>
          <h1 className={`text-4xl sm:text-5xl font-semibold ${heading}`} style={{ ...headingStyle, color: '#2a1f16' }}>{sample.names}</h1>
          <p className="mt-3 text-sm sm:text-base" style={{ color: '#6f5a45' }}>{controls.formattedDate} · {sample.location}</p>
        </div>
      </section>
    );
  }

  if (templateMeta.hero === 'decorative' || templateMeta.hero === 'decorative-botanical') {
    return (
      <section className="text-center px-6 py-10 relative overflow-hidden" data-testid="v2-builder-template-hero-decorative">
        {templateMeta.hero === 'decorative-botanical' && (
          <>
            <span className="absolute left-3 top-2 text-5xl opacity-20">❀</span>
            <span className="absolute right-3 bottom-1 text-5xl opacity-20">❋</span>
          </>
        )}
        <p className="text-xs uppercase tracking-[0.2em] mb-2">{templateMeta.name}</p>
        <h1 className={`text-5xl font-semibold ${heading}`} style={headingStyle}>{sample.names}</h1>
        <p className="mt-2 text-sm">{controls.formattedDate} · {sample.location}</p>
      </section>
    );
  }

  return (
    <section className="px-6 py-10" data-testid="v2-builder-template-hero-text">
      <p className="text-xs uppercase tracking-[0.2em] mb-2">Editorial Elegant</p>
      <h1 className={`text-5xl font-semibold ${heading}`} style={headingStyle}>{sample.names}</h1>
      <p className="mt-2 text-stone-600">{controls.formattedDate} · {sample.location}</p>
    </section>
  );
};

const TemplateNavigation = ({ templateMeta, pageMode, activePage, setActivePage }) => {
  if (pageMode !== 'multi') return null;

  const items = [
    { key: 'home', label: 'Home' },
    { key: 'story', label: 'Story' },
    { key: 'details', label: 'Details' },
    { key: 'rsvp', label: 'RSVP' },
  ];

  if (templateMeta.nav === 'minimal-scroll') {
    return (
      <nav className="sticky top-0 z-20 bg-white border-b border-stone-200 px-4 py-2" data-testid="v2-builder-nav-minimal-scroll">
        <div className="flex items-center gap-4 text-sm overflow-x-auto">
          {items.map((item) => (
            <button key={item.key} onClick={() => setActivePage(item.key)} className={`pb-1 border-b-2 ${activePage === item.key ? 'border-black font-semibold' : 'border-transparent'}`} data-testid={`v2-builder-page-tab-${item.key}`}>
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    );
  }

  if (templateMeta.nav === 'center-formal') {
    return (
      <nav className="text-center py-3 border-y border-stone-200" data-testid="v2-builder-nav-center-formal">
        {items.map((item, index) => (
          <span key={item.key}>
            <button onClick={() => setActivePage(item.key)} className={`mx-2 text-sm ${activePage === item.key ? 'underline font-semibold' : ''}`} data-testid={`v2-builder-page-tab-${item.key}`}>{item.label}</button>
            {index < items.length - 1 && <span className="text-stone-400">·</span>}
          </span>
        ))}
      </nav>
    );
  }

  if (templateMeta.nav === 'flat-bar') {
    return (
      <nav className="sticky top-0 z-20 bg-slate-50 border-b border-slate-200 px-4 py-2" data-testid="v2-builder-nav-flat-bar">
        <div className="flex gap-2 overflow-x-auto">
          {items.map((item) => (
            <button key={item.key} onClick={() => setActivePage(item.key)} className={`px-3 py-1 text-xs border ${activePage === item.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300'}`} data-testid={`v2-builder-page-tab-${item.key}`}>{item.label}</button>
          ))}
        </div>
      </nav>
    );
  }

  if (templateMeta.nav === 'elegant-top') {
    return (
      <nav className="text-center border-y border-emerald-200 py-2" data-testid="v2-builder-nav-elegant-top">
        <div className="flex justify-center flex-wrap gap-4 text-sm">
          {items.map((item) => (
            <button key={item.key} onClick={() => setActivePage(item.key)} className={`${activePage === item.key ? 'font-semibold underline' : ''}`} data-testid={`v2-builder-page-tab-${item.key}`}>{item.label}</button>
          ))}
        </div>
      </nav>
    );
  }

  if (templateMeta.nav === 'luxury-line') {
    return (
      <nav className="border-y px-4 py-3" style={{ borderColor: '#d6c5ae', backgroundColor: '#fffaf4' }} data-testid="v2-builder-nav-luxury-line">
        <div className="flex gap-5 overflow-x-auto text-xs uppercase tracking-[0.14em]">
          {items.map((item) => (
            <button key={item.key} onClick={() => setActivePage(item.key)} className={`pb-1 border-b ${activePage === item.key ? 'font-semibold border-[#5f4630]' : 'border-transparent'}`} data-testid={`v2-builder-page-tab-${item.key}`}>
              {item.label}
            </button>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <nav className="border-y border-stone-200 px-4 py-2" data-testid="v2-builder-nav-top-underlined">
      <div className="flex gap-4 text-sm overflow-x-auto">
        {items.map((item) => (
          <button key={item.key} onClick={() => setActivePage(item.key)} className={`pb-1 ${activePage === item.key ? 'border-b-2 border-black font-semibold' : 'border-b-2 border-transparent'}`} data-testid={`v2-builder-page-tab-${item.key}`}>{item.label}</button>
        ))}
      </div>
    </nav>
  );
};

export const WeddingTemplateV2Workspace = ({ templateKey }) => {
  const { token, isAuthenticated } = useAuth();
  const templateMeta = TEMPLATE_META[templateKey] || TEMPLATE_META['editorial-elegant'];
  const [isDesktop, setIsDesktop] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 1024 : true));
  const [pageMode, setPageMode] = useState('single');
  const [activePage, setActivePage] = useState('home');
  const [dateFormat, setDateFormat] = useState('uk-long');
  const [showCountdown, setShowCountdown] = useState(true);
  const [typographyStyle, setTypographyStyle] = useState('timeless-seriffed');
  const [spacingStyle, setSpacingStyle] = useState('airy');
  const [uppercaseHeadings, setUppercaseHeadings] = useState(false);
  const [imageOrientation, setImageOrientation] = useState('horizontal');
  const [imageFit, setImageFit] = useState('fill');
  const [colorTheme, setColorTheme] = useState('stone-ivory');
  const [activeStylePack, setActiveStylePack] = useState('editorial');
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [settingsHydrated, setSettingsHydrated] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState('');
  const [savedStyleSignature, setSavedStyleSignature] = useState('');
  const [lockSavingMood, setLockSavingMood] = useState('');
  const [lockedMoodFeedback, setLockedMoodFeedback] = useState('');
  const [savedIndicatorHighlight, setSavedIndicatorHighlight] = useState(false);
  const [publishingWebsite, setPublishingWebsite] = useState(false);
  const [publishedLiveUrl, setPublishedLiveUrl] = useState('');
  const [coupleNames, setCoupleNames] = useState('');
  const [weddingDateISO, setWeddingDateISO] = useState('');
  const [weddingLocation, setWeddingLocation] = useState('');
  const [storyText, setStoryText] = useState('');
  const [travelInfoText, setTravelInfoText] = useState('');
  const [photoSharingMessage, setPhotoSharingMessage] = useState('Share your favorite moments from our wedding day.');
  const [timelineItems, setTimelineItems] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [sectionImages, setSectionImages] = useState(DEFAULT_SECTION_IMAGES);
  const [editingSectionIndex, setEditingSectionIndex] = useState(0);
  const [uploadingCategory, setUploadingCategory] = useState('');
  const skipNextAutoSaveRef = useRef(true);
  const lockFeedbackTimeoutRef = useRef(null);
  const savedIndicatorTimeoutRef = useRef(null);
  const lastMicrocopyAuditCountRef = useRef('');

  const sectionTypeSet = useMemo(() => new Set(SECTION_OPTIONS.map((option) => option.value)), []);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobilePanelOpen(false);
    }
  }, [isDesktop]);

  const formattedDate = useMemo(() => formatWeddingDate(weddingDateISO, dateFormat), [dateFormat, weddingDateISO]);
  const countdownDays = useMemo(() => {
    if (!weddingDateISO) return 0;
    const today = new Date();
    const wedding = new Date(weddingDateISO);
    if (Number.isNaN(wedding.getTime())) return 0;
    const diff = wedding.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [weddingDateISO]);

  const selectedTheme = useMemo(
    () => COLOR_THEME_OPTIONS.find((option) => option.value === colorTheme) || COLOR_THEME_OPTIONS[0],
    [colorTheme],
  );

  const previewSectionGapClass = useMemo(() => {
    const spacingMap = {
      compact: 'space-y-4',
      balanced: 'space-y-5',
      airy: 'space-y-7',
      'airy-plus': 'space-y-10',
    };
    return spacingMap[spacingStyle] || 'space-y-6';
  }, [spacingStyle]);

  const controls = {
    formattedDate,
    countdownDays,
    showCountdown,
    typographyStyle,
    spacingStyle,
    uppercaseHeadings,
    imageOrientation,
    imageFit,
  };

  const dynamicSample = useMemo(() => ({
    names: coupleNames || 'Your Names',
    dateISO: weddingDateISO,
    location: weddingLocation,
    story: storyText,
    travel: travelInfoText,
    photoCta: photoSharingMessage,
    timeline: timelineItems,
    heroImage: galleryImages[0] || '',
    gallery: galleryImages,
    sectionImages,
    details: {
      ceremonyTime: weddingDateISO ? formatWeddingDate(weddingDateISO, 'uk-long') : '',
      ceremonyPlace: weddingLocation,
      receptionTime: weddingDateISO ? formatWeddingDate(weddingDateISO, 'uk-long') : '',
      receptionPlace: weddingLocation,
    },
  }), [coupleNames, weddingDateISO, weddingLocation, storyText, travelInfoText, photoSharingMessage, timelineItems, galleryImages, sectionImages]);

  const visibleSections = useMemo(() => {
    const structuredSections = sections.filter((section) => section.type !== 'title');
    if (pageMode === 'single') return structuredSections;
    return structuredSections.filter((section) => pageForSectionType(section.type) === activePage);
  }, [sections, pageMode, activePage]);

  const preserveScrollWhileUpdating = (updater) => {
    if (typeof window === 'undefined') {
      updater();
      return;
    }

    const previousY = window.scrollY;
    updater();
    window.requestAnimationFrame(() => {
      if (Math.abs(window.scrollY - previousY) > 2) {
        window.scrollTo({ top: previousY, behavior: 'auto' });
      }
    });
  };

  const updateFieldWithoutScrollJump = (setter, value) => {
    preserveScrollWhileUpdating(() => setter(value));
  };

  const removeHeroImage = () => {
    setGalleryImages([]);
  };

  const removeSectionImage = (sectionType) => {
    setSectionImages((prev) => ({
      ...prev,
      [sectionType]: { url: '', orientation: 'landscape' },
    }));
  };

  const uploadSinglePhotoRequest = async (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    const response = await axios.post(`${API}/wedding/upload-photo`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data?.url || '';
  };

  const uploadSectionImage = async (file, sectionType) => {
    if (!file || !token || !isAuthenticated) return;
    try {
      setUploadingCategory(sectionType);
      const orientation = await detectImageOrientation(file);
      const uploadedUrl = await uploadSinglePhotoRequest(file, 'story');
      if (!uploadedUrl) return;
      setSectionImages((prev) => ({
        ...prev,
        [sectionType]: {
          url: uploadedUrl,
          orientation,
        },
      }));
    } catch (error) {
      toast.error('Could not upload section image right now.');
    } finally {
      setUploadingCategory('');
    }
  };

  const updateTimelineItem = (index, field, value) => {
    preserveScrollWhileUpdating(() => {
      setTimelineItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
    });
  };

  const addTimelineItem = () => {
    setTimelineItems((prev) => [...prev, { time: '', title: '', note: '' }]);
  };

  const removeTimelineItem = (index) => {
    setTimelineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadPhotosForBuilder = async (files, category = 'main') => {
    if (!token || !isAuthenticated) {
      toast.error('Please sign in before uploading photos.');
      return;
    }

    const incoming = Array.from(files || []);
    if (!incoming.length) return;

    setUploadingCategory(category);
    try {
      const firstFile = incoming[0];
      const uploadedUrl = await uploadSinglePhotoRequest(firstFile, category === 'main' ? 'main' : 'gallery');
      if (uploadedUrl) {
        setGalleryImages([uploadedUrl]);
      }
    } catch (error) {
      toast.error('Could not upload photo right now.');
    } finally {
      setUploadingCategory('');
    }
  };

  const applyStylePack = (packValue) => {
    const pack = STYLE_PACK_OPTIONS.find((item) => item.value === packValue);
    if (!pack) return;
    setActiveStylePack(pack.value);
    setColorTheme(pack.colorTheme);
    setTypographyStyle(pack.typographyStyle);
    setSpacingStyle(pack.spacingStyle);
  };

  const saveBuilderSettings = async ({ manual = false, styleOverride = null } = {}) => {
    if (!token) return;

    const payloadStylePack = styleOverride?.stylePack || activeStylePack;
    const payloadColorTheme = styleOverride?.colorTheme || colorTheme;
    const payloadTypography = styleOverride?.typographyStyle || typographyStyle;
    const payloadSpacing = styleOverride?.spacingStyle || spacingStyle;

    const payload = {
      template_key: templateKey,
      style_pack: payloadStylePack,
      page_mode: pageMode,
      date_format: dateFormat,
      show_countdown: showCountdown,
      typography_style: payloadTypography,
      spacing_style: payloadSpacing,
      uppercase_headings: uppercaseHeadings,
      image_orientation: imageOrientation,
      image_fit: imageFit,
      color_theme: payloadColorTheme,
      sections: sections.map((section) => section.type).filter((type) => sectionTypeSet.has(type)),
    };

    setIsSavingSettings(true);
    try {
      await axios.put(`${API}/planner/website/builder-v2-settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const nowIso = new Date().toISOString();
      setLastSavedAt(nowIso);
      setSavedStyleSignature(
        buildStyleSignature({
          stylePack: payload.style_pack,
          color: payload.color_theme,
          typography: payload.typography_style,
          spacing: payload.spacing_style,
        }),
      );
      setSavedIndicatorHighlight(true);
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current);
      }
      savedIndicatorTimeoutRef.current = setTimeout(() => setSavedIndicatorHighlight(false), 1500);
      if (manual) {
        toast.success('Builder style settings saved.');
      }
      return true;
    } catch (error) {
      if (manual) {
        toast.error('Could not save builder settings right now.');
      }
      return false;
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleLockMood = async (packValue) => {
    const pack = STYLE_PACK_OPTIONS.find((item) => item.value === packValue);
    if (!pack || !token || !isAuthenticated) return;

    applyStylePack(pack.value);
    setLockSavingMood(pack.value);
    const didSave = await saveBuilderSettings({
      manual: false,
      styleOverride: {
        stylePack: pack.value,
        colorTheme: pack.colorTheme,
        typographyStyle: pack.typographyStyle,
        spacingStyle: pack.spacingStyle,
      },
    });
    setLockSavingMood('');

    if (didSave) {
      setLockedMoodFeedback(pack.value);
      if (lockFeedbackTimeoutRef.current) {
        clearTimeout(lockFeedbackTimeoutRef.current);
      }
      lockFeedbackTimeoutRef.current = setTimeout(() => setLockedMoodFeedback(''), 1500);
    }
  };

  const handlePublishWebsite = async () => {
    if (!token || !isAuthenticated) {
      toast.error('Please sign in as a couple before publishing.');
      return;
    }

    setPublishingWebsite(true);
    try {
      const response = await axios.post(
        `${API}/wedding/publish`,
        {
          template_id: templateKey,
          style_settings: {
            template_key: templateKey,
            style_pack: activeStylePack,
            page_mode: pageMode,
            date_format: dateFormat,
            show_countdown: showCountdown,
            typography_style: typographyStyle,
            spacing_style: spacingStyle,
            uppercase_headings: uppercaseHeadings,
            image_orientation: imageOrientation,
            image_fit: imageFit,
            color_theme: colorTheme,
            sections: sections.map((section) => section.type),
          },
          content: {
            couple_names: coupleNames,
            date: weddingDateISO,
            location: weddingLocation,
            story: storyText,
            schedule: timelineItems.map((item) => ({ time: item.time, title: item.title, notes: item.note })),
            travel_info: travelInfoText,
            main_photo: galleryImages[0] || '',
            photos: galleryImages,
            story_images: sectionImages.story?.url ? [sectionImages.story.url] : [],
            details_image: sectionImages.wedding_details?.url || '',
            schedule_image: sectionImages.timeline?.url || '',
            travel_image: sectionImages.text_block?.url || '',
            photo_sharing_message: photoSharingMessage,
          },
          rsvp_enabled: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const liveUrl = response.data?.live_url || '';
      setPublishedLiveUrl(liveUrl);
      toast.success('Your wedding website is live');
    } catch (error) {
      toast.error('Could not publish your wedding website right now.');
    } finally {
      setPublishingWebsite(false);
    }
  };

  const handleCopyPublishedUrl = async () => {
    if (!publishedLiveUrl) return;
    try {
      await navigator.clipboard.writeText(publishedLiveUrl);
      toast.success('Live URL copied');
    } catch (error) {
      toast.error('Could not copy URL automatically.');
    }
  };

  useEffect(() => {
    if (!token) {
      setSettingsHydrated(true);
      return;
    }

    const loadBuilderSettings = async () => {
      try {
        const response = await axios.get(`${API}/planner/website/builder-v2-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const saved = response.data?.settings;
        if (saved) {
          if (saved.page_mode) setPageMode(saved.page_mode);
          if (saved.date_format) setDateFormat(saved.date_format);
          if (typeof saved.show_countdown === 'boolean') setShowCountdown(saved.show_countdown);
          if (saved.typography_style) setTypographyStyle(saved.typography_style);
          if (saved.spacing_style) setSpacingStyle(saved.spacing_style);
          if (typeof saved.uppercase_headings === 'boolean') setUppercaseHeadings(saved.uppercase_headings);
          if (saved.image_orientation) setImageOrientation(saved.image_orientation);
          if (saved.image_fit) setImageFit(saved.image_fit);
          if (saved.color_theme) setColorTheme(saved.color_theme);
          if (saved.style_pack) setActiveStylePack(saved.style_pack);
          setSavedStyleSignature(
            buildStyleSignature({
              stylePack: saved.style_pack,
              color: saved.color_theme,
              typography: saved.typography_style,
              spacing: saved.spacing_style,
            }),
          );
          setSections(DEFAULT_SECTIONS);
        }
        else {
          setSavedStyleSignature(
            buildStyleSignature({
              stylePack: activeStylePack,
              color: colorTheme,
              typography: typographyStyle,
              spacing: spacingStyle,
            }),
          );
        }
      } catch (error) {
        toast.error('Could not load saved builder styles.');
      } finally {
        setSettingsHydrated(true);
        skipNextAutoSaveRef.current = true;
      }
    };

    loadBuilderSettings();
  }, [sectionTypeSet, token]);

  const controlsReady = !token || settingsHydrated;
  const themeLuminance = useMemo(() => getRelativeLuminance(selectedTheme.pageBg), [selectedTheme.pageBg]);
  const adaptiveStatusTextTone = useMemo(() => {
    if (themeLuminance > 0.88) {
      return blendHex(selectedTheme.pageText, selectedTheme.border, 0.4);
    }
    return '#5C0120';
  }, [selectedTheme.border, selectedTheme.pageText, themeLuminance]);
  const adaptiveStatusBorderTone = useMemo(() => {
    if (themeLuminance > 0.88) {
      return blendHex(selectedTheme.border, selectedTheme.pageText, 0.18);
    }
    return blendHex('#5C0120', selectedTheme.pageBg, 0.52);
  }, [selectedTheme.border, selectedTheme.pageBg, selectedTheme.pageText, themeLuminance]);
  const adaptiveSavedDotTone = useMemo(() => {
    if (themeLuminance > 0.88) {
      return blendHex(selectedTheme.pageText, selectedTheme.border, 0.4);
    }
    return '#5C0120';
  }, [selectedTheme.border, selectedTheme.pageText, themeLuminance]);
  const adaptiveHelperMicrocopyStyle = useMemo(
    () => ({ color: adaptiveStatusTextTone, opacity: 0.84 }),
    [adaptiveStatusTextTone],
  );
  const adaptiveEmptyStateStyle = useMemo(
    () => ({ color: adaptiveStatusTextTone, opacity: 0.8 }),
    [adaptiveStatusTextTone],
  );
  const currentMoodLabel = useMemo(
    () => STYLE_PACK_OPTIONS.find((pack) => pack.value === activeStylePack)?.label || 'Custom',
    [activeStylePack],
  );
  const currentStyleSignature = useMemo(
    () => buildStyleSignature({ stylePack: activeStylePack, color: colorTheme, typography: typographyStyle, spacing: spacingStyle }),
    [activeStylePack, colorTheme, spacingStyle, typographyStyle],
  );
  const stylePreviewDirty = Boolean(token && settingsHydrated && savedStyleSignature && currentStyleSignature !== savedStyleSignature);

  useEffect(() => {
    const matchingPack = STYLE_PACK_OPTIONS.find(
      (pack) =>
        pack.colorTheme === colorTheme
        && pack.typographyStyle === typographyStyle
        && pack.spacingStyle === spacingStyle,
    );
    setActiveStylePack(matchingPack ? matchingPack.value : 'custom');
  }, [colorTheme, spacingStyle, typographyStyle]);

  useEffect(() => {
    if (!settingsHydrated || !token) return;
    if (stylePreviewDirty) return;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      saveBuilderSettings({ manual: false });
    }, 900);

    return () => clearTimeout(timeoutId);
  }, [
    activeStylePack,
    colorTheme,
    dateFormat,
    imageFit,
    imageOrientation,
    pageMode,
    sections,
    settingsHydrated,
    showCountdown,
    spacingStyle,
    stylePreviewDirty,
    templateKey,
    token,
    typographyStyle,
    uppercaseHeadings,
  ]);

  useEffect(() => () => {
    if (lockFeedbackTimeoutRef.current) {
      clearTimeout(lockFeedbackTimeoutRef.current);
    }
    if (savedIndicatorTimeoutRef.current) {
      clearTimeout(savedIndicatorTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return;

    const auditMicrocopy = () => {
      const helperNodes = Array.from(document.querySelectorAll(MICROCOPY_AUDIT_SELECTOR));
      if (!helperNodes.length) {
        console.warn('[Microcopy Audit] No non-critical helper nodes found for audit.');
        return;
      }

      const nextCount = String(helperNodes.length);
      const globalCountKey = '__gweMicrocopyAuditCount';
      const previousGlobalCount = window[globalCountKey];
      if (lastMicrocopyAuditCountRef.current !== nextCount && previousGlobalCount !== nextCount) {
        // Keep this exact log string for QA grep stability: [Microcopy Audit] audited helper nodes: <count>
        console.log(`[Microcopy Audit] audited helper nodes: ${helperNodes.length}`);
        lastMicrocopyAuditCountRef.current = nextCount;
        window[globalCountKey] = nextCount;
      }

      helperNodes.forEach((node) => {
        const role = node.getAttribute('data-microcopy-role');
        if (role !== 'non-critical-helper') {
          console.warn('[Microcopy Audit] Missing non-critical helper role marker:', node);
        }

        const computed = window.getComputedStyle(node);
        const opacity = Number.parseFloat(computed.opacity || '1');
        if (opacity > 0.92) {
          console.warn('[Microcopy Audit] Helper microcopy opacity is high; consider subtler tone:', node);
        }
      });
    };

    const timeoutId = window.setTimeout(auditMicrocopy, 0);
    return () => window.clearTimeout(timeoutId);
  }, [activeStylePack, colorTheme, controlsReady, sections, spacingStyle, typographyStyle]);

  const ControlPanelContent = () => (
    <div className="space-y-5">
      <div className="space-y-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C0120] font-semibold">Wedding Builder · Preview</p>
          <h1 className="text-2xl font-semibold text-stone-900">{templateMeta.name}</h1>
          <p className="text-sm text-stone-600">{templateMeta.description}</p>
        </div>
        <div className="border border-stone-200 bg-stone-50 p-3" data-testid="v2-builder-persistence-box">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-stone-600" data-testid="v2-builder-persistence-status">
              {!isAuthenticated
                ? 'Sign in as a couple to save these style settings.'
                : (stylePreviewDirty
                    ? 'Preview mode active. Tap Save style to keep this mood.'
                    : (lastSavedAt
                    ? `Last saved ${new Date(lastSavedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                    : 'Autosave is active. Changes save to your wedding profile.'))}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => saveBuilderSettings({ manual: true })}
              disabled={!isAuthenticated || isSavingSettings || !settingsHydrated}
              data-testid="v2-builder-manual-save-button"
            >
              {isSavingSettings ? 'Saving...' : 'Save style'}
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="v2-builder-publish-actions-wrap">
            <Button
              type="button"
              size="sm"
              onClick={handlePublishWebsite}
              disabled={!isAuthenticated || publishingWebsite}
              className="bg-[#5C0120] hover:bg-[#5C0120]/90 text-white"
              data-testid="v2-builder-publish-website-button"
            >
              {publishingWebsite ? 'Publishing…' : 'Publish website'}
            </Button>
            {publishedLiveUrl && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCopyPublishedUrl}
                  data-testid="v2-builder-copy-live-url-button"
                >
                  Copy URL
                </Button>
                <a href={publishedLiveUrl} target="_blank" rel="noreferrer" data-testid="v2-builder-open-live-website-link">
                  <Button type="button" size="sm" variant="outline">Open website</Button>
                </a>
              </>
            )}
          </div>
          {publishedLiveUrl && (
            <p className="mt-2 text-xs text-stone-600 break-all" data-testid="v2-builder-live-url-display">Your website link: {publishedLiveUrl}</p>
          )}
        </div>
      </div>

      {!controlsReady && (
        <div className="border border-stone-200 bg-stone-50 p-2 text-xs" style={adaptiveHelperMicrocopyStyle} data-testid="v2-builder-controls-loading-note" data-microcopy-role="non-critical-helper">
          Loading saved style settings...
        </div>
      )}

      <div className={`space-y-5 ${controlsReady ? '' : 'opacity-60 pointer-events-none'}`} data-testid="v2-builder-controls-ready-wrap">
      <section className="space-y-2" data-testid="v2-mood-preview-carousel-wrap">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold flex items-center gap-2"><Palette className="h-4 w-4" />Quick Mood Preview</p>
          <p className="text-[11px]" style={{ color: adaptiveStatusTextTone, opacity: 0.88 }} data-testid="v2-mood-preview-active-label">
            {activeStylePack && activeStylePack !== 'custom'
              ? `Selected: ${STYLE_PACK_OPTIONS.find((pack) => pack.value === activeStylePack)?.label || 'Mood'}`
              : 'Selected: Custom'}
          </p>
        </div>
        <p className="text-xs" style={adaptiveHelperMicrocopyStyle} data-testid="v2-mood-preview-helper-text" data-microcopy-role="non-critical-helper">Swipe to preview. Tap any mood to apply instantly.</p>
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1" data-testid="v2-mood-preview-carousel-track">
          {STYLE_PACK_OPTIONS.map((pack) => {
            const active = activeStylePack === pack.value;
            return (
              <div
                key={pack.value}
                onClick={() => applyStylePack(pack.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    applyStylePack(pack.value);
                  }
                }}
                role="button"
                tabIndex={0}
                className={`min-w-[72%] sm:min-w-[46%] snap-start border p-3 text-left transition-colors ${active ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-style-pack-card-${pack.value}`}
              >
                <p className="text-xs uppercase tracking-[0.14em] text-stone-500">{pack.label}</p>
                <p className="text-sm font-medium mt-1" style={adaptiveHelperMicrocopyStyle} data-testid={`v2-style-pack-helper-${pack.value}`} data-microcopy-role="non-critical-helper">{pack.helper}</p>
                <p className="text-[11px] mt-1" style={adaptiveHelperMicrocopyStyle}>Spacing: {pack.spacingStyle.replace('-', ' ')}</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleLockMood(pack.value);
                    }}
                    disabled={!isAuthenticated || lockSavingMood === pack.value || !controlsReady}
                    className="text-[11px] uppercase tracking-[0.12em] text-stone-700 hover:text-stone-900 disabled:opacity-50"
                    data-testid={`v2-style-pack-lock-${pack.value}`}
                  >
                    {lockSavingMood === pack.value ? 'Saving...' : 'Lock this mood'}
                  </button>
                  <span
                    className={`text-[11px] transition-opacity ${lockedMoodFeedback === pack.value ? 'opacity-100' : 'opacity-0'}`}
                    style={{ color: adaptiveStatusTextTone }}
                    data-testid={`v2-style-pack-lock-feedback-${pack.value}`}
                  >
                    Mood saved
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-layout-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><LayoutPanelTop className="h-4 w-4" />Page Structure</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {LAYOUT_OPTIONS.map((option) => {
            const Icon = option.icon;
            const active = pageMode === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setPageMode(option.value)}
                className={`text-left rounded-xl border p-3 transition ${active ? 'border-[#5C0120] bg-[#5C0120]/5 shadow-sm' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-layout-card-${option.value}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="h-4 w-4" />
                  <p className="font-semibold text-sm">{option.label}</p>
                </div>
                <p className="text-xs" style={adaptiveHelperMicrocopyStyle} data-testid={`v2-layout-helper-${option.value}`} data-microcopy-role="non-critical-helper">{option.helper}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-date-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><CalendarDays className="h-4 w-4" />Date Format</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {DATE_OPTIONS.map((option) => {
            const active = dateFormat === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setDateFormat(option.value)}
                className={`rounded-xl border p-3 text-left transition ${active ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-date-card-${option.value}`}
              >
                <p className="text-[11px] uppercase tracking-[0.14em] text-stone-500">{option.label}</p>
                <p className="text-sm font-medium mt-1">{option.preview}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-typography-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><Type className="h-4 w-4" />Typography Style</p>
        <div className="grid grid-cols-1 gap-2">
          {TYPOGRAPHY_OPTIONS.map((option) => {
            const active = typographyStyle === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setTypographyStyle(option.value)}
                className={`rounded-xl border p-3 text-left transition ${active ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-typography-card-${option.value}`}
              >
                <p className="text-xs uppercase tracking-[0.14em] text-stone-500">{option.label}</p>
                <p className="mt-1" style={option.style}>Sofia & Daniel</p>
                <p className="text-xs mt-1" style={adaptiveHelperMicrocopyStyle} data-testid={`v2-typography-helper-${option.value}`} data-microcopy-role="non-critical-helper">{option.helper}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-color-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><Palette className="h-4 w-4" />Color Mood</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {COLOR_THEME_OPTIONS.map((option) => {
            const active = colorTheme === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setColorTheme(option.value)}
                className={`border p-3 text-left transition ${active ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-color-theme-card-${option.value}`}
              >
                <p className="text-xs uppercase tracking-[0.14em] text-stone-500">{option.label}</p>
                <div className="flex items-center gap-1 mt-2">
                  {option.preview.map((swatch, idx) => (
                    <span key={`${option.value}-${idx}`} className="h-6 w-6 border border-white/60" style={{ backgroundColor: swatch }} />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-spacing-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><Columns2 className="h-4 w-4" />Spacing Style</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SPACING_STYLE_OPTIONS.map((option) => {
            const active = spacingStyle === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSpacingStyle(option.value)}
                className={`border p-3 text-left transition ${active ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                data-testid={`v2-spacing-style-card-${option.value}`}
              >
                <p className="text-xs uppercase tracking-[0.14em] text-stone-500">{option.label}</p>
                <p className="text-xs mt-1" style={adaptiveHelperMicrocopyStyle} data-testid={`v2-spacing-helper-${option.value}`} data-microcopy-role="non-critical-helper">{option.helper}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-control-toggle-cards-wrap">
        <p className="text-sm font-semibold flex items-center gap-2"><Settings2 className="h-4 w-4" />Wedding Controls</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setShowCountdown((v) => !v)}
            className={`rounded-xl border p-3 text-left ${showCountdown ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white'}`}
            data-testid="v2-countdown-card-toggle"
          >
            <p className="text-xs uppercase tracking-[0.14em] text-stone-500">Countdown</p>
            <p className="text-sm font-medium mt-1">{showCountdown ? 'On' : 'Off'}</p>
          </button>
          <button
            type="button"
            onClick={() => setUppercaseHeadings((v) => !v)}
            className={`rounded-xl border p-3 text-left ${uppercaseHeadings ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white'}`}
            data-testid="v2-uppercase-card-toggle"
          >
            <p className="text-xs uppercase tracking-[0.14em] text-stone-500">Uppercase Headings</p>
            <p className="text-sm font-medium mt-1">{uppercaseHeadings ? 'Enabled' : 'Disabled'}</p>
          </button>
        </div>
      </section>

      <section className="space-y-2" data-testid="v2-section-builder-visual-grid-wrap">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Your Page Sections</p>
          <p className="text-xs text-stone-500" data-testid="v2-fixed-structure-note">Hero, Story, Details, Schedule, Travel</p>
        </div>
      </section>

      <section className="space-y-3" data-testid="v2-section-order-visual-list-wrap">
        <div className="space-y-3">
          {sections.map((section, index) => {
            const meta = SECTION_TILE_META[section.type];
            const Icon = meta?.icon || Plus;
            const previewText = section.type === 'story'
              ? (storyText ? storyText.slice(0, 60) : 'Add your story')
              : section.type === 'photo_gallery'
                ? `${galleryImages.length} photos`
                : section.type === 'timeline'
                  ? `${timelineItems.length} schedule items`
                  : section.type === 'text_block'
                    ? (travelInfoText ? travelInfoText.slice(0, 40) : 'Add travel info')
                    : section.type === 'title'
                      ? `${coupleNames}`
                      : `${meta?.mini || 'Section preview'}`;

            return (
              <div key={section.id} className={`rounded-2xl border bg-white/95 p-4 space-y-3 shadow-sm ${editingSectionIndex === index ? 'border-[#5C0120]' : 'border-stone-200'}`} data-testid={`v2-section-order-card-${index}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-stone-600" />
                    <p className="text-xs font-semibold">{meta?.label || section.type}</p>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setEditingSectionIndex(index)} className="px-2 py-1 border border-stone-300 rounded text-xs" data-testid={`v2-control-section-edit-${index}`}>Edit</button>
                  </div>
                </div>
                <p className="text-xs text-stone-500 leading-relaxed" data-testid={`v2-control-section-preview-${index}`}>{previewText}</p>
              </div>
            );
          })}
        </div>

        {sections[editingSectionIndex] && (
          <div className="rounded-2xl border border-stone-200 bg-stone-50/70 p-4 space-y-4" data-testid="v2-section-editor-panel">
            <div className="border-b border-stone-200 pb-3">
              <p className="text-xs uppercase tracking-[0.14em] text-stone-500">Section editor</p>
              <p className="text-base font-semibold mt-1">{SECTION_TILE_META[sections[editingSectionIndex].type]?.label || 'Section'}</p>
            </div>

            {sections[editingSectionIndex].type === 'title' && (
              <>
                <div>
                  <Label htmlFor="v2-couple-names-input">Couple names</Label>
                  <Input id="v2-couple-names-input" value={coupleNames} onChange={(e) => updateFieldWithoutScrollJump(setCoupleNames, e.target.value)} data-testid="v2-couple-names-input" />
                </div>
                <div>
                  <Label htmlFor="v2-wedding-date-input">Wedding date</Label>
                  <Input id="v2-wedding-date-input" type="date" value={weddingDateISO} onChange={(e) => updateFieldWithoutScrollJump(setWeddingDateISO, e.target.value)} data-testid="v2-wedding-date-input" />
                </div>
                <div>
                  <Label htmlFor="v2-wedding-location-input">Location</Label>
                  <Input id="v2-wedding-location-input" value={weddingLocation} onChange={(e) => updateFieldWithoutScrollJump(setWeddingLocation, e.target.value)} data-testid="v2-wedding-location-input" />
                </div>
                <div>
                  <Label htmlFor="v2-hero-image-upload">Hero image</Label>
                  <Input id="v2-hero-image-upload" type="file" accept="image/*" onChange={(e) => uploadPhotosForBuilder(e.target.files, 'main')} data-testid="v2-hero-image-upload-input" />
                  {uploadingCategory === 'main' && <p className="text-xs text-stone-500 mt-1">Uploading hero image...</p>}
                  {galleryImages[0] && (
                    <div className="mt-2 rounded-xl border border-stone-200 p-2 bg-white" data-testid="v2-hero-image-thumb-wrap">
                      <img src={galleryImages[0]} alt="Hero" className="w-full h-28 object-cover rounded-lg" />
                      <button type="button" className="mt-2 text-[11px] border px-2 py-1 rounded" onClick={removeHeroImage} data-testid="v2-hero-image-remove-button">Remove</button>
                    </div>
                  )}
                </div>
              </>
            )}

            {sections[editingSectionIndex].type === 'story' && (
              <>
                <div>
                  <Label htmlFor="v2-story-textarea">Your Story</Label>
                  <Textarea id="v2-story-textarea" rows={6} placeholder="Tell your story — how you met, your journey, and your special moments" value={storyText} onChange={(e) => updateFieldWithoutScrollJump(setStoryText, e.target.value)} data-testid="v2-story-textarea" />
                </div>
                <div>
                  <Label htmlFor="v2-story-image-upload">Story image (one)</Label>
                  <Input id="v2-story-image-upload" type="file" accept="image/*" onChange={(e) => uploadSectionImage(e.target.files?.[0], 'story')} data-testid="v2-story-image-upload-input" />
                  {uploadingCategory === 'story' && <p className="text-xs text-stone-500 mt-1">Uploading story image...</p>}
                </div>
                {sectionImages.story?.url && (
                  <div className="rounded-xl border border-stone-200 p-2 bg-white" data-testid="v2-story-thumb-0">
                    <img src={sectionImages.story.url} alt="Story" className="w-full h-28 object-cover rounded-lg" />
                    <div className="flex flex-wrap gap-1 mt-2">
                      <button type="button" className="text-[10px] border px-2 py-1 rounded" onClick={() => removeSectionImage('story')} data-testid="v2-story-image-remove-button">Remove</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {sections[editingSectionIndex].type === 'wedding_details' && (
              <>
                <div>
                  <Label htmlFor="v2-details-image-upload">Details image (one)</Label>
                  <Input id="v2-details-image-upload" type="file" accept="image/*" onChange={(e) => uploadSectionImage(e.target.files?.[0], 'wedding_details')} data-testid="v2-details-image-upload-input" />
                </div>
                {uploadingCategory === 'wedding_details' && <p className="text-xs text-stone-500">Uploading details image...</p>}
                {sectionImages.wedding_details?.url && (
                  <div className="rounded-xl border border-stone-200 p-2 bg-white" data-testid="v2-details-image-thumb-wrap">
                    <img src={sectionImages.wedding_details.url} alt="Details" className="w-full h-28 object-cover rounded-lg" />
                    <button type="button" className="mt-2 text-[10px] border px-2 py-1 rounded" onClick={() => removeSectionImage('wedding_details')} data-testid="v2-details-image-remove-button">Remove</button>
                  </div>
                )}
              </>
            )}

            {sections[editingSectionIndex].type === 'timeline' && (
              <>
                <div>
                  <Label htmlFor="v2-schedule-image-upload">Schedule image (one)</Label>
                  <Input id="v2-schedule-image-upload" type="file" accept="image/*" onChange={(e) => uploadSectionImage(e.target.files?.[0], 'timeline')} data-testid="v2-schedule-image-upload-input" />
                </div>
                {uploadingCategory === 'timeline' && <p className="text-xs text-stone-500">Uploading schedule image...</p>}
                {sectionImages.timeline?.url && (
                  <div className="rounded-xl border border-stone-200 p-2 bg-white" data-testid="v2-schedule-image-thumb-wrap">
                    <img src={sectionImages.timeline.url} alt="Schedule" className="w-full h-28 object-cover rounded-lg" />
                    <button type="button" className="mt-2 text-[10px] border px-2 py-1 rounded" onClick={() => removeSectionImage('timeline')} data-testid="v2-schedule-image-remove-button">Remove</button>
                  </div>
                )}

                {timelineItems.map((item, index) => (
                  <div key={`timeline-editor-${index}`} className="border border-stone-200 bg-white p-2 space-y-2" data-testid={`v2-timeline-editor-row-${index}`}>
                    <Input value={item.time} onChange={(e) => updateTimelineItem(index, 'time', e.target.value)} placeholder="Time" data-testid={`v2-timeline-time-${index}`} />
                    <Input value={item.title} onChange={(e) => updateTimelineItem(index, 'title', e.target.value)} placeholder="Title" data-testid={`v2-timeline-title-${index}`} />
                    <Input value={item.note} onChange={(e) => updateTimelineItem(index, 'note', e.target.value)} placeholder="Note" data-testid={`v2-timeline-note-${index}`} />
                    <Button type="button" variant="outline" size="sm" onClick={() => removeTimelineItem(index)} data-testid={`v2-timeline-remove-${index}`}>Remove</Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addTimelineItem} data-testid="v2-timeline-add-item">Add schedule item</Button>
              </>
            )}

            {sections[editingSectionIndex].type === 'text_block' && (
              <>
                <div>
                  <Label htmlFor="v2-travel-textarea">Travel Info</Label>
                  <Textarea id="v2-travel-textarea" rows={4} value={travelInfoText} onChange={(e) => updateFieldWithoutScrollJump(setTravelInfoText, e.target.value)} data-testid="v2-travel-textarea" />
                </div>
                <div>
                  <Label htmlFor="v2-travel-image-upload">Travel image (one)</Label>
                  <Input id="v2-travel-image-upload" type="file" accept="image/*" onChange={(e) => uploadSectionImage(e.target.files?.[0], 'text_block')} data-testid="v2-travel-image-upload-input" />
                  {uploadingCategory === 'text_block' && <p className="text-xs text-stone-500 mt-1">Uploading travel image...</p>}
                  {sectionImages.text_block?.url && (
                    <div className="mt-2 rounded-xl border border-stone-200 p-2 bg-white" data-testid="v2-travel-image-thumb-wrap">
                      <img src={sectionImages.text_block.url} alt="Travel" className="w-full h-28 object-cover rounded-lg" />
                      <button type="button" className="mt-2 text-[10px] border px-2 py-1 rounded" onClick={() => removeSectionImage('text_block')} data-testid="v2-travel-image-remove-button">Remove</button>
                    </div>
                  )}
                </div>
              </>
            )}

            {sections[editingSectionIndex].type === 'photo_upload' && (
              <div>
                <Label htmlFor="v2-photo-sharing-message-input">Photo sharing message</Label>
                <Input id="v2-photo-sharing-message-input" value={photoSharingMessage} onChange={(e) => updateFieldWithoutScrollJump(setPhotoSharingMessage, e.target.value)} data-testid="v2-photo-sharing-message-input" />
              </div>
            )}
          </div>
        )}
      </section>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100" data-testid="v2-builder-workspace-page">
      <div className="container-wedding py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">
          {isDesktop && (
            <aside className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="v2-builder-controls-panel-desktop">
              <ControlPanelContent />
            </aside>
          )}

          <section className="space-y-4" data-testid="v2-builder-preview-area">
            <div
              className="overflow-hidden border border-stone-200"
              style={{ backgroundColor: selectedTheme.pageBg, color: selectedTheme.pageText }}
              data-testid="v2-builder-template-preview-shell"
            >
              <div className="flex justify-end px-4 pt-3" data-testid="v2-current-mood-badge-wrap">
                <span
                  className="inline-flex items-center gap-2 border bg-white/80 px-3 py-1 text-[11px] uppercase tracking-[0.14em]"
                  style={{ borderColor: adaptiveStatusBorderTone, color: adaptiveStatusTextTone }}
                  data-testid="v2-current-mood-badge"
                >
                  Current mood: {currentMoodLabel}
                  {isAuthenticated && Boolean(savedStyleSignature) && !stylePreviewDirty && (
                    <span
                      className={`h-1.5 w-1.5 rounded-full transition-opacity duration-[1500ms] ${savedIndicatorHighlight ? 'opacity-100' : 'opacity-65'}`}
                      style={{ backgroundColor: adaptiveSavedDotTone }}
                      data-testid="v2-current-mood-saved-dot"
                    />
                  )}
                </span>
              </div>
              <TemplateHero templateMeta={templateMeta} controls={controls} theme={selectedTheme} sample={dynamicSample} />
              <TemplateNavigation templateMeta={templateMeta} pageMode={pageMode} activePage={activePage} setActivePage={setActivePage} />
              <div className={`p-4 sm:p-6 ${previewSectionGapClass}`} data-testid="v2-builder-preview-sections-wrap">
                {visibleSections.length === 0 ? (
                  <div
                    className="border border-dashed border-stone-300 bg-white/70 p-4 text-sm"
                    style={adaptiveEmptyStateStyle}
                    data-testid="v2-builder-empty-sections-hint"
                    data-microcopy-role="non-critical-helper"
                  >
                    No sections selected yet. Add sections from the panel to continue editing.
                  </div>
                ) : (
                  visibleSections.map((section, sectionIndex) => (
                    <div key={section.id}>{renderSection({ section, sectionIndex, controls, templateMeta, theme: selectedTheme, sample: dynamicSample })}</div>
                  ))
                )}
              </div>
              <footer className="text-center text-xs py-7" data-testid="v2-builder-footer">Powered by Gibraltar Wedding Expo</footer>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white p-4" data-testid="v2-builder-desktop-check-note">
              <p className="text-sm text-stone-700">
                Your website preview updates live as you edit each section.
              </p>
            </div>

            <div className="fixed bottom-4 left-4 z-30">
              <Link to="/wedding-template-previews-v2" className="px-3 py-2 rounded-full bg-black text-white text-xs" data-testid="v2-back-to-gallery-link">
                Back to v2 gallery
              </Link>
            </div>

            {!isDesktop && (
              <button
                type="button"
                className="fixed bottom-4 right-4 z-30 rounded-full bg-[#5C0120] text-white px-4 py-3 shadow-lg flex items-center gap-2"
                onClick={() => setMobilePanelOpen(true)}
                data-testid="v2-open-mobile-controls-button"
              >
                <PanelBottomOpen className="h-4 w-4" />
                Edit
              </button>
            )}

            {!isDesktop && mobilePanelOpen && (
              <div className="lg:hidden fixed inset-x-0 bottom-0 z-40" data-testid="v2-mobile-controls-drawer-wrap">
                <div className="mx-auto w-full max-h-[70vh] rounded-t-2xl border border-stone-200 bg-white shadow-[0_-12px_40px_rgba(28,25,23,0.18)] overflow-auto">
                  <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-stone-200 px-4 py-3">
                    <div className="w-10 h-1 rounded-full bg-stone-300 mx-auto mb-3" />
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Design Panel</h2>
                      <button type="button" onClick={() => setMobilePanelOpen(false)} className="p-2 rounded border border-stone-300" data-testid="v2-close-mobile-controls-button">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <ControlPanelContent />
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export const V2_TEMPLATE_LIST = [
  { key: 'editorial-elegant', name: 'Editorial Elegant', description: TEMPLATE_META['editorial-elegant'].description },
  { key: 'photo-story', name: 'Photo Story', description: TEMPLATE_META['photo-story'].description },
  { key: 'classic-invitation', name: 'Classic Invitation', description: TEMPLATE_META['classic-invitation'].description },
  { key: 'modern-clean', name: 'Modern Clean', description: TEMPLATE_META['modern-clean'].description },
  { key: 'modern-luxury', name: 'Modern Luxury', description: TEMPLATE_META['modern-luxury'].description },
];
